import { AlertTriangle, X } from "lucide-react";
import { useCallback, useEffect } from "react";
import { Link } from "react-router";
import { Button } from "./Button";

/**
 * Story 2261 — shown when a regenerate / generate mutation returns
 * `status: "manual_edits_present"`. Names what will be lost, optionally
 * deep-links to the manually-edited surface, and surfaces a Confirm button
 * that retries the underlying mutation with `confirmOverwrite: true`.
 */
interface RegenerateConfirmDialogProps {
  open: boolean;
  warning: string;
  title?: string;
  confirmLabel?: string;
  deepLinkPath?: string | null;
  deepLinkLabel?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RegenerateConfirmDialog({
  open,
  warning,
  title = "Manual edits will be replaced",
  confirmLabel = "Regenerate anyway",
  deepLinkPath,
  deepLinkLabel,
  onCancel,
  onConfirm,
}: RegenerateConfirmDialogProps) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onCancel();
    },
    [onCancel],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      data-testid="regenerate-confirm-dialog"
    >
      {/* biome-ignore lint/a11y/noStaticElementInteractions: backdrop dismiss overlay */}
      <div
        className="absolute inset-0 bg-black/30"
        onClick={onCancel}
        onKeyDown={(e) => {
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="relative flex w-full max-w-md flex-col rounded-lg bg-card shadow-xl">
        <div className="flex items-start justify-between border-b border-border px-6 py-4">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} className="text-amber-500" />
            <h2 className="text-sm font-semibold text-text-primary">{title}</h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded p-1 text-text-faint hover:bg-card-hover hover:text-text-secondary"
            aria-label="Close dialog"
          >
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 px-6 py-4 text-sm text-text-secondary">
          <p>{warning}</p>
          {deepLinkPath && deepLinkLabel && (
            <p className="mt-3">
              <Link className="text-link underline" to={deepLinkPath}>
                {deepLinkLabel}
              </Link>
            </p>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 py-4">
          <Button variant="secondary" onClick={onCancel} data-testid="regenerate-confirm-cancel">
            Cancel
          </Button>
          <Button variant="primary" onClick={onConfirm} data-testid="regenerate-confirm-proceed">
            {confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
